import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { djs } from '@duke-hq/djs';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  ChevronDown,
  Circle,
  CircleCheckBig,
  Clock,
  CirclePlus,
  Download,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import type { PrePlacementDocumentType } from '@duke-hq/svc-api-db';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { AddRequirementModal } from './AddRequirementModal';
import { getDocumentTypeLabel } from './documentTypeLabels';
import { RejectDocumentModal } from './RejectDocumentModal';
import { UploadDocumentModal } from './UploadDocumentModal';
import { testIds } from './testIds';

type ChecklistItem = RouterOutputs['workPlacements']['placements']['getChecklist']['items'][number];

// Sort order for the checklist: what needs the admin's action (pending review)
// first, then what is still outstanding, then resolved items, with accepted
// (fully done) last.
function checklistAttentionRank(status: ChecklistItem['status']): number {
  switch (status) {
    case 'PENDING_REVIEW':
      return 0;
    case 'NOT_SUBMITTED':
      return 1;
    case 'REJECTED':
      return 2;
    default:
      return 3;
  }
}

function StatusIcon({ status }: { status: ChecklistItem['status'] }): React.ReactElement {
  const iconProps = { size: pxToRem(20), strokeWidth: 2 } as const;
  return (
    <Box
      color={status === 'ACCEPTED' ? 'info' : 'fg.subtle'}
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxSize={5}
      flexShrink={0}
    >
      {status === 'ACCEPTED' ? (
        <CircleCheckBig {...iconProps} />
      ) : status === 'PENDING_REVIEW' ? (
        <Clock {...iconProps} />
      ) : (
        <Circle {...iconProps} />
      )}
    </Box>
  );
}

function StatusBadge({
  status,
  isReadOnly = false,
}: {
  status: ChecklistItem['status'];
  isReadOnly?: boolean;
}): React.ReactElement {
  const isAccepted = status === 'ACCEPTED';
  const isPending = status === 'PENDING_REVIEW';
  const isRejected = status === 'REJECTED';

  // On a terminal (read-only) placement a still-pending document can never be
  // reviewed, so render it neutrally rather than the actionable blue.
  const showPendingAsNeutral = isPending && isReadOnly;

  const colorScheme = isAccepted
    ? 'green'
    : showPendingAsNeutral
      ? 'gray'
      : isPending
        ? 'blue'
        : isRejected
          ? 'red'
          : 'yellow';
  const bg = isAccepted
    ? 'bg.success-subtle'
    : showPendingAsNeutral
      ? 'bg.subtle'
      : isPending
        ? 'bg.info-subtle'
        : isRejected
          ? 'bg.danger-subtle'
          : 'bg.warning-subtle';

  return (
    <Badge
      variant="outline"
      colorScheme={colorScheme}
      bg={bg}
      borderRadius="full"
      px={2}
      textTransform="none"
      fontWeight="medium"
    >
      {isAccepted ? (
        <Trans>Accepted</Trans>
      ) : isPending ? (
        <Trans>Pending review</Trans>
      ) : isRejected ? (
        <Trans>Rejected</Trans>
      ) : (
        <Trans>Not submitted</Trans>
      )}
    </Badge>
  );
}

function ChecklistRow({
  item,
  journeyId,
  canEdit,
  onSuccess,
  onUpload,
}: {
  item: ChecklistItem;
  journeyId: string;
  canEdit: boolean;
  onSuccess: () => void;
  onUpload: () => void;
}) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const labels = getDocumentTypeLabel();
  const isAccepted = item.status === 'ACCEPTED';

  const [loadingDrns, setLoadingDrns] = useState<Set<string>>(new Set());
  const getDownloadUrl = trpcAdmin.files.getDownloadUrl.useMutation();

  const handleDownload = async (fileDrn: string) => {
    setLoadingDrns((prev) => new Set(prev).add(fileDrn));
    try {
      const { url } = await getDownloadUrl.mutateAsync({ fileDrn });
      window.open(url, '_blank');
    } catch (error) {
      toast({
        status: 'error',
        title: t`Failed to download file`,
        description: error instanceof Error ? error.message : t`Unknown error`,
      });
    } finally {
      setLoadingDrns((prev) => {
        const next = new Set(prev);
        next.delete(fileDrn);
        return next;
      });
    }
  };

  const removeMutation = trpcAdmin.workPlacements.placements.removePlacementRequirement.useMutation(
    {
      onSuccess: () => {
        toast({ status: 'success', title: t`Requirement removed` });
        onSuccess();
      },
      onError: (error) => {
        toast({
          status: 'error',
          title: t`Failed to remove requirement`,
          description: error.message,
        });
      },
    },
  );

  const acceptMutation = trpcAdmin.workPlacements.placements.reviewDocument.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Document accepted` });
      onSuccess();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to accept document`, description: error.message });
    },
  });

  return (
    <Box
      data-testid={testIds.checklistItem}
      py={3}
      px={4}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      bg="bg.canvas"
      opacity={canEdit ? 1 : 0.6}
    >
      <HStack gap={3} align="center">
        <Box flexShrink={0}>
          <StatusIcon status={item.status} />
        </Box>
        <Stack flex={1} gap={1}>
          <Text fontSize="md" fontWeight="normal" color={isAccepted ? 'info' : 'fg.default'}>
            {labels[item.documentType]}
          </Text>
          {item.reviewNotes != null ? (
            item.status === 'REJECTED' ? (
              <Box bg="bg.danger-subtle" borderRadius="md" px={3} py={2}>
                <Text fontSize="xs" color="fg.danger">
                  <Text as="span" fontWeight="semibold">
                    <Trans>Rejection note:</Trans>
                  </Text>{' '}
                  {item.reviewNotes}
                </Text>
              </Box>
            ) : (
              <Text fontSize="xs" color="fg.muted">
                {item.reviewNotes}
              </Text>
            )
          ) : null}
        </Stack>
        <HStack gap={2} flexShrink={0} align="center">
          {item.fileDrns.map((drn) => (
            <IconButton
              key={drn}
              aria-label={t`Download document`}
              icon={<Download size={pxToRem(16)} />}
              size="xs"
              variant="ghost"
              colorScheme="blue"
              isLoading={loadingDrns.has(drn)}
              onClick={() => handleDownload(drn)}
            />
          ))}
          {canEdit && item.status === 'PENDING_REVIEW' ? null : (
            <StatusBadge status={item.status} isReadOnly={!canEdit} />
          )}
          {canEdit && item.status === 'PENDING_REVIEW' ? (
            <>
              <Button
                data-testid={testIds.acceptButton}
                size="sm"
                colorScheme="green"
                variant="outline"
                isLoading={acceptMutation.isPending}
                onClick={() =>
                  acceptMutation.mutate({
                    journeyId,
                    documentType: item.documentType,
                    action: 'ACCEPT',
                  })
                }
              >
                <Trans>Accept</Trans>
              </Button>
              <Button
                data-testid={testIds.rejectButton}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={onOpen}
              >
                <Trans>Reject</Trans>
              </Button>
              <RejectDocumentModal
                isOpen={isOpen}
                onClose={onClose}
                journeyId={journeyId}
                documentType={item.documentType}
                onSuccess={() => {
                  onClose();
                  onSuccess();
                }}
              />
            </>
          ) : null}
          {canEdit &&
          (item.status === 'NOT_SUBMITTED' || item.status === 'REJECTED' || item.isAdditional) ? (
            <HStack gap={0} flexShrink={0}>
              {item.status === 'NOT_SUBMITTED' || item.status === 'REJECTED' ? (
                <Tooltip label={t`Upload on behalf of student`} hasArrow>
                  <IconButton
                    data-testid={testIds.uploadDocumentButton}
                    size="sm"
                    variant="ghost"
                    icon={<Upload size={iconSize.md} />}
                    onClick={onUpload}
                    aria-label={t`Upload on behalf of student`}
                  />
                </Tooltip>
              ) : null}
              {item.isAdditional ? (
                <Tooltip label={t`Remove requirement`} hasArrow>
                  <IconButton
                    data-testid={testIds.removeRequirementButton}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    isLoading={removeMutation.isPending}
                    onClick={() =>
                      removeMutation.mutate({
                        journeyId,
                        documentType: item.documentType,
                      })
                    }
                    icon={<Trash2 size={iconSize.md} />}
                    aria-label={t`Remove requirement`}
                  />
                </Tooltip>
              ) : null}
            </HStack>
          ) : null}
        </HStack>
      </HStack>
    </Box>
  );
}

function SectionTitle(): React.ReactElement {
  return (
    <Text fontSize="lg" fontWeight="semibold">
      <Trans>Required Checks</Trans>
    </Text>
  );
}

function PlacementChecklist({
  journeyId,
  courseEnrolmentId,
  prePlacementChecklistDueAt,
  canEdit,
  onDueDateUpdated,
  showDueDate,
  journeyPlaced,
}: {
  journeyId: string;
  courseEnrolmentId: number;
  prePlacementChecklistDueAt: Date | null;
  canEdit: boolean;
  onDueDateUpdated: () => void;
  showDueDate: boolean;
  journeyPlaced: boolean;
}): React.ReactElement {
  const toast = useToast();
  const addRequirementModal = useDisclosure();
  const uploadDocumentModal = useDisclosure();
  const [rowUploadDocType, setRowUploadDocType] = useState<PrePlacementDocumentType | null>(null);
  const rowUploadModal = useDisclosure();

  const openRowUpload = useCallback(
    (documentType: PrePlacementDocumentType) => {
      setRowUploadDocType(documentType);
      rowUploadModal.onOpen();
    },
    [rowUploadModal],
  );

  const { data, isLoading, refetch } = trpcAdmin.workPlacements.placements.getChecklist.useQuery({
    journeyId,
  });

  const utils = trpcAdmin.useUtils();

  const refreshAfterReview = (): void => {
    void refetch();
    void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
  };

  const updatePrePlacementChecklistDueAt =
    trpcAdmin.workPlacements.placements.updatePrePlacementChecklistDueAt.useMutation({
      onSuccess() {
        toast({ status: 'success', title: t`Checklist due date updated` });
        onDueDateUpdated();
      },
      onError() {
        toast({ status: 'error', title: t`Failed to update checklist due date` });
      },
    });

  const items = data?.items ?? [];
  const completedCount = items.filter((i) => i.status === 'ACCEPTED').length;

  const sortedItems = [...items].sort(
    (a, b) => checklistAttentionRank(a.status) - checklistAttentionRank(b.status),
  );

  const uploadableTypes = items
    .filter((i) => i.status === 'NOT_SUBMITTED' || i.status === 'REJECTED')
    .map((i) => i.documentType);

  const currentDueDateValue =
    prePlacementChecklistDueAt != null
      ? djs(prePlacementChecklistDueAt).tz('Australia/Sydney').format('YYYY-MM-DD')
      : '';

  return (
    <>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <HStack gap={2} align="baseline">
          <SectionTitle />
          <Text fontSize="sm" color="fg.muted">
            {completedCount}/{items.length} <Trans>Complete</Trans>
          </Text>
        </HStack>
        <HStack gap={2} align="center">
          {showDueDate ? (
            <>
              <Text fontSize="sm" color="fg.muted">
                <Trans>Due date:</Trans>
              </Text>
              {canEdit ? (
                <Input
                  key={currentDueDateValue}
                  type="date"
                  size="sm"
                  w="auto"
                  defaultValue={currentDueDateValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === currentDueDateValue) {
                      return;
                    }

                    updatePrePlacementChecklistDueAt.mutate({
                      journeyId,
                      prePlacementChecklistDueAt:
                        value !== '' ? djs(value).startOf('day').toDate() : null,
                    });
                  }}
                />
              ) : (
                <Text fontSize="sm" fontWeight="medium">
                  {prePlacementChecklistDueAt != null
                    ? djs(prePlacementChecklistDueAt).tz('Australia/Sydney').format('DD MMM YYYY')
                    : '—'}
                </Text>
              )}
            </>
          ) : null}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              fontWeight="semibold"
              size="sm"
              rightIcon={<ChevronDown size={iconSize.sm} />}
            >
              <Trans>Actions</Trans>
            </MenuButton>
            <MenuList>
              <MenuItem
                icon={<Upload size={iconSize.sm} />}
                data-testid={testIds.uploadDocumentHeaderButton}
                onClick={uploadDocumentModal.onOpen}
                isDisabled={uploadableTypes.length === 0}
              >
                <Trans>Upload document</Trans>
              </MenuItem>
              <MenuItem
                icon={<CirclePlus size={iconSize.sm} />}
                data-testid={testIds.addRequirementButton}
                onClick={addRequirementModal.onOpen}
                isDisabled={journeyPlaced}
              >
                <Trans>Add Requirement</Trans>
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {isLoading ? (
        <Center p={4}>
          <Spinner size="md" />
        </Center>
      ) : items.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          <Trans>No pre-placement requirements set for this course.</Trans>
        </Text>
      ) : (
        <Stack gap={2}>
          {sortedItems.map((item) => (
            <ChecklistRow
              key={item.documentType}
              item={item}
              journeyId={journeyId}
              canEdit={canEdit}
              onSuccess={refreshAfterReview}
              onUpload={() => openRowUpload(item.documentType)}
            />
          ))}
        </Stack>
      )}

      <AddRequirementModal
        isOpen={addRequirementModal.isOpen}
        onClose={addRequirementModal.onClose}
        journeyId={journeyId}
        existingTypes={items.map((i) => i.documentType)}
        onSuccess={refreshAfterReview}
      />

      <UploadDocumentModal
        journeyId={journeyId}
        documentType={null}
        availableTypes={uploadableTypes}
        isOpen={uploadDocumentModal.isOpen}
        onClose={uploadDocumentModal.onClose}
        onSuccess={() => {
          uploadDocumentModal.onClose();
          refetch();
        }}
      />

      {rowUploadDocType != null ? (
        <UploadDocumentModal
          journeyId={journeyId}
          documentType={rowUploadDocType}
          availableTypes={[rowUploadDocType]}
          isOpen={rowUploadModal.isOpen}
          onClose={rowUploadModal.onClose}
          onSuccess={() => {
            rowUploadModal.onClose();
            refetch();
          }}
        />
      ) : null}
    </>
  );
}

export function RequiredChecksSection({
  journeyId,
  courseEnrolmentId,
  enrolmentWithdrawn,
  prePlacementChecklistDueAt = null,
  canEdit,
  onDueDateUpdated = () => {},
  showDueDate = true,
  journeyPlaced = false,
}: {
  journeyId: string | null;
  courseEnrolmentId: number;
  enrolmentWithdrawn: boolean;
  prePlacementChecklistDueAt?: Date | null;
  canEdit: boolean;
  onDueDateUpdated?: () => void;
  showDueDate?: boolean;
  journeyPlaced?: boolean;
}): React.ReactElement {
  return (
    <Stack
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      p={5}
      gap={4}
      data-testid={testIds.requiredChecksSection}
    >
      {journeyId == null || enrolmentWithdrawn ? (
        <>
          <SectionTitle />
          <Text fontSize="sm" color="fg.muted">
            {enrolmentWithdrawn ? (
              <Trans>This enrolment has been withdrawn.</Trans>
            ) : (
              <Trans>No placement for this student yet.</Trans>
            )}
          </Text>
        </>
      ) : (
        <PlacementChecklist
          journeyId={journeyId}
          courseEnrolmentId={courseEnrolmentId}
          prePlacementChecklistDueAt={prePlacementChecklistDueAt}
          canEdit={canEdit}
          onDueDateUpdated={onDueDateUpdated}
          showDueDate={showDueDate}
          journeyPlaced={journeyPlaced}
        />
      )}
    </Stack>
  );
}
